
import { Box, Stack } from "@mui/material"
import { useTranslation } from 'react-i18next';
import {AppContext, Layer, Shape} from "../../context/context";
import {useContext} from "react";
import {IconButton} from "@mui/material";
import EditableTypography from "../base_components/EditableTypography";

import { useDrag, useDrop, ConnectDropTarget } from 'react-dnd';
import {useEditLayer, useDeleteLayer} from "./functions/layerFunctions";
import {v4 as uuid4} from 'uuid';

import markerType from './functions/markerType'; 
import { useSelectElement } from './functions/selectShapeMethod';
import { logEvent } from '../../services/analytics';
import { collectTripPointIds } from '../map/functions/missionControlTripStyles';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
// Helper function to get vertex count for different shape types
function getVertexCount(shape: Shape): number {
    switch (shape.type) {
        case 'point':
            return 1; // Points always have 1 vertex
        case 'line':
            return shape.coordinates.length;
        case 'polygon':
            // For polygons, sum up all rings (exterior + holes)
            return shape.coordinates.reduce((total, ring) => total + ring.length, 0);
        default:
            return 0;
    }
}

type ShapeIconProps = {
    shapeData: Shape,
    layerId: number,
    showName?: boolean
}

const ShapeIcon: React.FC<ShapeIconProps> = ({
                    shapeData,
                    layerId,
                    showName = true
                    }) => {
    const { t } = useTranslation('mapDialogs');
    const editLayer = useEditLayer();
    const selectElement = useSelectElement(shapeData);

    const context = useContext(AppContext);
    if (!context) {
        throw new Error("ShapeIcon must be used within the AppContext provider")
    }

    const {global} = context;
    const isSelected = global.selectedShapes.includes(shapeData.id)
    const currentLayer: Layer = global.mapLayers.filter(layer => layer.layerId === layerId)[0] //todo: add a check to ensure that this is not empty

    const shapeIndex: number = currentLayer.shapes.findIndex(shape => shape.id === shapeData.id)
    const tripPointIds = collectTripPointIds(currentLayer.shapes);
    const hasLinkedTripPoints = tripPointIds.size > 0;

    const [{ isDragging }, dragRef] = useDrag({
        type: 'PLACE_MARK',
        item: { shapeIndex, layerId, shapeData },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
    });

    const [{ isOver }, dropRef] = useDrop<{ shapeIndex: number, layerId: number, shapeData: Shape }, void, { isOver: boolean }>({
        accept: 'PLACE_MARK',
        collect: (monitor) => ({
            isOver: monitor.isOver()
        }),
        drop: (item) => {
            // console.log('Dropped at index:', shapeIndex);
        }
    });

    function editShapeLable(shapeData: Shape, layerId: number) {
        function subFunction(value: string) {
            // --- Analytics: Log label edit ---
            logEvent({
                category: 'Shape',
                action: 'ShapeLabelEdited',
                label: 'Shape Label Edited',
                shapeType: shapeData.type,
                shapeId: shapeData.id,
                oldLabel: shapeData.name,
                newLabel: value,
                vertexCount: getVertexCount(shapeData)
            });

            const newShape = {
                ...shapeData,
                name: value
            }

            const layer: Layer = global.mapLayers.filter(l => l.layerId === layerId)[0]
            const shapeIndex: number = layer.shapes.findIndex(shape => shape.id === shapeData.id)
            
            const newLayer = {
                ...layer,
                shapes: [
                    ...layer.shapes.slice(0, shapeIndex),
                    newShape,
                    ...layer.shapes.slice(shapeIndex+1)
                ]
            }

            editLayer(newLayer, newLayer.layerId)
            
        }
        return subFunction
    }

    function deleteShape(shapeData: Shape, layerId: number) {
        // --- Analytics: Log shape deletion ---
        logEvent({
            category: 'Shape',
            action: 'ShapeDeleted',
            label: 'Shape Deleted',
            shapeType: shapeData.type,
            shapeId: shapeData.id,
            name: shapeData.name,
            vertexCount: getVertexCount(shapeData)
        });
        const layer: Layer = global.mapLayers.filter(l => l.layerId === layerId)[0]
        const newLayer = {
            ...layer,
            shapes: layer.shapes.filter(shape => shape.id !== shapeData.id)
        }
        editLayer(newLayer, newLayer.layerId)
    }

    return(
        <div ref={(node) => {
            dragRef(node);
            dropRef(node as unknown as HTMLDivElement);
        }}>
            <Stack
            direction='row'
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            onClick={selectElement}
            style={{
                backgroundColor: isSelected ? '#e0e0e0' : 'transparent',
                cursor: 'pointer',
                opacity: isDragging ? 0.5 : 1,
                border: isOver ? '2px dashed blue' : 'none',
                transition: 'opacity 0.2s ease, border 0.2s ease, background-color 0.2s ease',
            }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {markerType(shapeData, isSelected, {
                        isTripPointLinked: shapeData.type === 'point' ? tripPointIds.has(shapeData.id) : false,
                        allowColorFallback: !hasLinkedTripPoints,
                    })}
                    {showName && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <EditableTypography variant='body1' setValueFunction={editShapeLable(shapeData, layerId)} style={{ color: isSelected ? 'blue' : 'inherit' }}>
                                {shapeData.name === "" ? t('shapes.objectFallback', { id: shapeData.id }) : shapeData.name}
                            </EditableTypography>
                        </div>
                    )}
                </Box>
            </Stack>
        </div>
    )

}

export default ShapeIcon

